import { Avatar, Col, Input, Row, Text } from 'components';
import { useInfiniteScroll } from 'core';
import { Api, capitalize, formatCurrencyAmount } from 'lib';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableComponents } from 'screens/Admin/components';
import { STATUS_STYLES } from './helpers';
import SubscriberPanel from './SubscriberPanel';

const FETCH_LIMIT = 30;

let timer;

const StatusBadge = ({ status, label }) => {
  const normalizedStatus = status === 'success' ? 'active' : status;
  const s = STATUS_STYLES[normalizedStatus] || STATUS_STYLES.failed;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-md border ${s.bg} ${s.text} ${s.border}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {label || capitalize(normalizedStatus || '')}
    </span>
  );
};

const Subscribers = () => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [subscribers, setSubscribers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);
  const [error, setError] = useState(false);
  const fetching = useRef(false);
  const hasMore = useRef(true);
  const skip = useRef(0);
  const scrollId = 'subscribers-scroll';

  useInfiniteScroll(scrollId, fetchSubscribers);

  useEffect(() => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fetchSubscribers(true);
    }, 300);
  }, [text]);

  useEffect(() => {
    skip.current = subscribers?.length || 0;
  }, [subscribers]);

  async function fetchSubscribers(isFilterChange) {
    if (isFilterChange) {
      setSubscribers(null);
      setError(false);
      hasMore.current = true;
    }

    if (fetching.current) return hasMore.current ? FETCH_LIMIT : 0;
    if (!isFilterChange && !hasMore.current) return 0;
    fetching.current = true;
    setLoading(true);

    try {
      const res = await Api.post('/admin/subscriptionPlans/searchSubscribers', {
        text,
        skip: isFilterChange ? 0 : skip.current,
        limit: FETCH_LIMIT
      });

      if (!res) {
        setError(true);
        return 0;
      }

      const newData = res.data || [];
      hasMore.current = newData.length === FETCH_LIMIT;

      if (isFilterChange) {
        setSubscribers(newData);
      } else {
        setSubscribers((prev) => [...(prev || []), ...newData]);
      }

      return hasMore.current ? newData.length : 0;
    } catch {
      setError(true);
      return 0;
    } finally {
      fetching.current = false;
      setLoading(false);
    }
  }

  return (
    <Col start noFlex wid="100%">
      <Row start gap="30px" pad="12px 24px" noFlex>
        <Col noFlex>
          <Input
            big
            search
            pad="0 24px"
            wid="427px"
            placeholder={t('subscriptions.admin.subscribers.searchPlaceholder')}
            value={text}
            onChange={({ target }) => setText(target.value)}
          />
        </Col>
      </Row>

      <Col
        start
        wid="100%"
        id={scrollId}
        style={{ overflow: 'auto', maxHeight: 'calc(100vh - 170px)' }}
      >
        {error && !subscribers?.length ? (
          <Col wid="100%" marg="80px 0" centerAll noFlex>
            <Text className="text-[13px] text-[#97A3B5]">
              {t('subscriptions.admin.subscribers.error')}
            </Text>
          </Col>
        ) : !loading && subscribers && subscribers.length === 0 ? (
          <Col wid="100%" marg="80px 0" centerAll noFlex>
            <Text className="text-[13px] text-[#97A3B5]">
              {text
                ? t('subscriptions.admin.subscribers.emptySearch')
                : t('subscriptions.admin.subscribers.empty')}
            </Text>
          </Col>
        ) : (
          <>
            <TableComponents.List
              loading={loading}
              Header={() => (
                <>
                  <TableComponents.Th first>
                    <Text>{t('subscriptions.admin.subscribers.subscriber')}</Text>
                  </TableComponents.Th>
                  <TableComponents.Th>
                    <Text>{t('subscriptions.admin.subscribers.team')}</Text>
                  </TableComponents.Th>
                  <TableComponents.Th>
                    <Text>{t('subscriptions.admin.subscribers.plan')}</Text>
                  </TableComponents.Th>
                  <TableComponents.Th>
                    <Text>{t('subscriptions.admin.subscribers.status')}</Text>
                  </TableComponents.Th>
                  <TableComponents.Th>
                    <Text>{t('subscriptions.admin.subscribers.provider')}</Text>
                  </TableComponents.Th>
                  <TableComponents.Th last>
                    <Text>{t('subscriptions.admin.subscribers.amount')}</Text>
                  </TableComponents.Th>
                </>
              )}
              Items={() =>
                subscribers
                  ? subscribers.map((item) => (
                      <TableComponents.Tr
                        key={item._id}
                        onClick={() => setSelectedSubscriptionId(item._id)}
                        isSelected={selectedSubscriptionId === item._id}
                      >
                        <TableComponents.Td
                          isSelected={selectedSubscriptionId === item._id}
                          first
                        >
                          <Row center noFlex>
                            <Avatar user={item.user} avatar={item.user?.avatar} tiny />
                            <Col noFlex marg="0 0 0 8px">
                              <Text>{item.user?.name || item.user?.username}</Text>
                              <Text className="text-[11px] text-[#97A3B5]">
                                {item.user?.email || ''}
                              </Text>
                            </Col>
                          </Row>
                        </TableComponents.Td>

                        <TableComponents.Td isSelected={selectedSubscriptionId === item._id}>
                          <Row center noFlex>
                            {item.team?.avatar && (
                              <Avatar user={item.team} avatar={item.team.avatar} tiny />
                            )}
                            <Text marg={item.team?.avatar ? '0 0 0 8px' : '0'}>
                              {item.team?.name || '-'}
                            </Text>
                          </Row>
                        </TableComponents.Td>

                        <TableComponents.Td isSelected={selectedSubscriptionId === item._id}>
                          <Text>{item.plan?.title || '-'}</Text>
                        </TableComponents.Td>

                        <TableComponents.Td isSelected={selectedSubscriptionId === item._id}>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <StatusBadge status={item.status} label={t(`subscriptions.admin.status.${item.status}`, { defaultValue: item.status })} />
                            {item.hasPendingRefund && (
                              <span
                                title={t('subscriptions.admin.subscribers.refund.badge')}
                                className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200"
                              >
                                {t('subscriptions.admin.subscribers.refund.badge')}
                              </span>
                            )}
                            {item.hasRecentSuccess && (
                              <span
                                title="Payment succeeded but subscription status not yet updated"
                                className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200"
                              >
                                Payment OK
                              </span>
                            )}
                          </div>
                        </TableComponents.Td>

                        <TableComponents.Td isSelected={selectedSubscriptionId === item._id}>
                          <Text>{capitalize(item.provider || '')}</Text>
                        </TableComponents.Td>

                        <TableComponents.Td
                          isSelected={selectedSubscriptionId === item._id}
                          last
                        >
                          <Text>
                            {item.amount != null
                              ? `${formatCurrencyAmount(item.amount, item.currency)}/${t(
                                  `subscriptions.admin.subscribers.detail.intervalShort.${item.interval === 'monthly' ? 'monthly' : 'yearly'}`
                                )}`
                              : '-'}
                          </Text>
                        </TableComponents.Td>
                      </TableComponents.Tr>
                    ))
                  : null
              }
            />
            {!loading && !error && subscribers?.length > 0 && !hasMore.current && (
              <Col wid="100%" centerAll noFlex marg="12px 0 16px">
                <Text className="text-[11px] uppercase tracking-[0.12em] text-[#97A3B5]">
                  {t('subscriptions.admin.subscribers.endOfList', {
                    defaultValue: 'End of list'
                  })}
                </Text>
              </Col>
            )}
          </>
        )}
      </Col>

      <SubscriberPanel
        subscriptionId={selectedSubscriptionId}
        closeModal={() => setSelectedSubscriptionId(null)}
      />
    </Col>
  );
};

export default Subscribers;
